import { useState } from "react";
import type { AssistDiff, AssistResult } from "../lib/api.ts";
import { t, type Key } from "../lib/i18n.ts";
import { elementLabel } from "../lib/ops.ts";
import type { EditorStore } from "../lib/store.ts";
import { Err, Spinner, msgOf, useApp } from "./common.tsx";
import { useStore } from "./hooks.ts";

const GROUPS = ["scenes_added", "scenes_removed", "scenes_changed", "elements_added", "elements_removed", "audio_added", "audio_removed", "audio_changed", "project_changed"] as const;

function DiffView({ d }: { d: AssistDiff }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {GROUPS.map((g) => d[g].length ? <div key={g}><h4 className="h">{t(`as.d_${g}` as Key)}</h4><ul className="dl">{d[g].map((x: string, i: number) => <li key={i}>{x}</li>)}</ul></div> : null)}
      {d.elements_changed.length > 0 && <div><h4 className="h">{t("as.d_elements_changed")}</h4>
        <ul className="dl">{d.elements_changed.map((e) => <li key={e.id}>{e.name} <small className="muted">· {e.fields.map((f) => f.replace("transform.", "")).join(", ")}</small></li>)}</ul></div>}
      {d.animations_changed.length > 0 && <div><h4 className="h">{t("as.d_animations_changed")}</h4>
        <ul className="dl">{d.animations_changed.map((a, i) => <li key={i}>{a.element} <small className="muted">· {a.property}</small></li>)}</ul></div>}
    </div>
  );
}

export function AssistPanel({ store }: { store: EditorStore }) {
  const { api, refreshMe, me } = useApp();
  const st = useStore(store);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null as string | null);
  const [res, setRes] = useState(null as null | { r: AssistResult; sent: unknown }); const [done, setDone] = useState(false);
  const p = st.project!, sel = st.selection;
  const focus = sel.kind === "element" ? { kind: "element", id: sel.id } : sel.kind === "scene" ? { kind: "scene", id: sel.id } : sel.kind === "audio" ? { kind: "audio", id: sel.id } : undefined;
  const focusName = sel.kind === "element" && p.elements[sel.id] ? elementLabel(p.elements[sel.id]!) : sel.kind === "scene" ? (p.scenes.find((s) => s.id === sel.id)?.name || t("insp.scene"))
    : sel.kind === "audio" ? (p.audio.find((a) => a.id === sel.id)?.name || t("au.clip")) : null;

  async function run(e: any) {
    e.preventDefault(); if (!text.trim() || busy) return;
    setBusy(true); setErr(null); setRes(null); setDone(false);
    const sent = store.getState().project!;
    try { const r = await api.assist(sent.id, text.trim(), sent, focus); void refreshMe(); setRes({ r, sent }); }
    catch (x) { setErr(msgOf(x)); } finally { setBusy(false); }
  }
  const stale = res !== null && store.getState().project !== res.sent;
  function apply() {
    if (!res || stale) return;
    const next = res.r.project;
    store.edit((cur) => ({ ...next, revision: cur.revision }));    // ONE undo step; autosave persists it
    setRes(null); setDone(true); setText("");
  }
  return (
    <div className="stack">
      <h3 className="h">{t("as.title")}</h3>
      <p className="muted" style={{ margin: 0 }}>{t("as.sub")}</p>
      <form className="stack" onSubmit={run}>
        {focusName && <small className="muted">{t("as.focus", { name: focusName })}</small>}
        <textarea className="textarea" aria-label={t("as.title")} maxLength={2000} placeholder={t("as.placeholder")} value={text} onChange={(e: any) => setText(e.target.value)} />
        <div className="chips">{(["as.s1", "as.s2", "as.s3"] as const).map((k) => <button type="button" className="chip" key={k} onClick={() => setText(t(k))}>{t(k)}</button>)}</div>
        <button className="btn primary" type="submit" disabled={busy || !text.trim()}>{busy && <Spinner />}{busy ? t("as.running") : t("as.run")}</button>
        {me && <small className="muted">{t("ai.quota", { used: me.quotas.ai_generations_used_today, max: me.quotas.ai_generations_per_day })}</small>}
      </form>
      <Err>{err}</Err>
      {done && <div className="alert" role="status">{t("as.applied")}</div>}
      {res && (
        <section className="card" aria-live="polite">
          <p style={{ margin: 0 }}>{res.r.summary}</p>
          {res.r.changed ? <DiffView d={res.r.diff} /> : <div className="muted">{t("as.no_change")}</div>}
          {res.r.warnings.length > 0 && <div className="alert warn"><strong>{t("ai.warnings")}</strong><ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{res.r.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}</ul></div>}
          {stale && <div className="alert warn" role="alert">{t("as.stale")}</div>}
          <small className="muted">{t("as.notice")} · {t("ai.provider", { name: res.r.provider })}</small>
          <div style={{ display: "flex", gap: 8 }}>
            {res.r.changed && <button className="btn primary" disabled={stale} onClick={apply}>{t("as.apply")}</button>}
            <button className="btn" onClick={() => setRes(null)}>{t("as.discard")}</button>
          </div>
        </section>)}
    </div>
  );
}
